/**
 * 엑셀자동화 실패 작업 → "조치 방법" 안내 데이터.
 *
 * 서버(Django·.NET 엑셀 서버)가 돌려준 error_message 문자열에 들어 있는 핵심 구절로
 * 오류 유형을 판별하고, 사용자가 스스로 고칠 수 있는 단계와 그림을 묶어 준다.
 * 어느 규칙에도 맞지 않으면 "일반" 안내로 떨어진다.
 *
 * 규칙 순서가 중요하다 — 위에서부터 처음 맞는 규칙을 쓴다. 예를 들어
 * "다른 통합문서를 참조하는 연결이 깨져 있어 저장할 수 없습니다" 는 '저장할 수 없' 보다
 * '통합문서를 참조' 가 먼저 걸려야 하므로 외부 연결 규칙이 저장 실패 규칙보다 앞에 있다.
 */

export type ExcelFixGuideKind =
  | "no_permission" // 이용권 없음
  | "input_missing" // 시트 미선택·파일정보 없음
  | "server_restart" // 서버 재시작으로 중단
  | "transient" // 내려받기 시간 초과 등 일시적 오류
  | "task_limit" // 동시 작업 상한
  | "range_too_large"
  | "time_limit" // 시트 범위 초과(1만 행·ZZ열)
  | "external_link" // 다른 통합문서 참조 연결
  | "drawing" // 그림·도형·개체
  | "protected" // 암호·보호된 파일
  | "cost_ratio_missing" // 원가계산서 구성비 열 없음
  | "statement_not_found" // 내역서 인식 안 됨
  | "save_failed" // 파일 형식·저장 실패
  | "general"; // 그 외

/** 모달이 그려 주는 그림 종류(외부 이미지 없이 HTML 표·SVG 로 그린다). */
export type ExcelFixFigure =
  | "header_structure" // 내역서 2줄 머리글 구조
  | "sheet_select" // 처리할 시트만 고르기
  | "ctrl_end" // Ctrl+End 로 멀리 떨어진 셀 찾기
  | "external_link"; // 데이터 › 연결 편집 › 연결 끊기 경로

/**
 * 재시도 버튼 정책.
 * - same_file: 파일 문제가 아니므로 기존 재시도(같은 파일로 다시 실행)로 바로 간다.
 * - fixed_file: 파일을 고쳐야 하므로 재시도 대신 "고친 파일을 다시 업로드" 를 안내한다.
 * - none: 재시도 자체가 의미 없음(이용권·입력 누락).
 */
export type ExcelFixRetryHint = "same_file" | "fixed_file" | "none";

export interface ExcelFixGuide {
  kind: ExcelFixGuideKind;
  /** 모달 제목(오류 유형 이름) */
  title: string;
  /** 왜 생기는지 한두 문장 */
  summary: string;
  /** "이렇게 해 보세요" 번호 단계 — 쉬운 말, 3~6개 */
  steps: string[];
  /** 그림. figureAfterStep(1부터)이 있으면 그 단계 바로 아래, 없으면 단계 목록 아래에 넣는다. */
  figure?: ExcelFixFigure;
  figureAfterStep?: number;
  retryHint: ExcelFixRetryHint;
  /** 이용권 페이지 같은 바로가기 */
  linkTo?: "payment";
}

export const EXCEL_FIX_GUIDES: Record<ExcelFixGuideKind, ExcelFixGuide> = {
  no_permission: {
    kind: "no_permission",
    title: "이용권이 필요한 기능이에요",
    summary: "이 작업은 엑셀자동화 이용권(엑셀올인원 또는 변경준공 패키지)이 있어야 실행됩니다.",
    steps: [
      "이용권 페이지에서 현재 쓰고 있는 이용권과 남은 기간을 확인합니다.",
      "계약내역서·기성내역서는 엑셀올인원 패키지, 변경준공·증감내역서는 변경준공 패키지로도 실행할 수 있습니다.",
      "이용권을 구매(또는 연장)한 뒤 같은 파일로 다시 실행합니다.",
    ],
    retryHint: "none",
    linkTo: "payment",
  },

  input_missing: {
    kind: "input_missing",
    title: "파일 또는 시트 선택이 빠졌어요",
    summary: "업로드한 파일 정보가 없거나, 처리할 시트가 선택되지 않은 채로 실행됐습니다.",
    steps: [
      "엑셀 파일을 다시 업로드합니다. (업로드 뒤 시트 목록이 뜰 때까지 기다려 주세요.)",
      "처리할 시트(내역서·집계표 등)를 체크한 뒤 실행합니다.",
      "같은 일이 반복되면 브라우저 새로고침 후 다시 시도합니다.",
    ],
    retryHint: "none",
  },

  server_restart: {
    kind: "server_restart",
    title: "서버 재시작으로 작업이 멈췄어요",
    summary: "파일 문제가 아닙니다. 처리 중에 서버가 재시작되어 작업이 끊겼을 뿐이므로 그대로 다시 실행하면 됩니다.",
    steps: [
      "아래 '같은 파일로 다시 실행' 버튼을 누르거나, 작업 행의 재시도 버튼을 누릅니다.",
      "여러 건이 함께 멈췄다면 한 건씩 재시도합니다.",
      "다시 실행해도 같은 오류가 나면 고객센터에 알려 주세요.",
    ],
    retryHint: "same_file",
  },

  transient: {
    kind: "transient",
    title: "일시적인 오류예요 (시간 초과)",
    summary: "파일을 내려받거나 처리하는 도중 잠깐 네트워크·서버 응답이 늦어져 중단됐습니다. 파일 문제가 아닌 경우가 대부분입니다.",
    steps: [
      "잠시(1~2분) 기다렸다가 같은 파일로 다시 실행합니다.",
      "파일이 아주 크면(수십 MB) 불필요한 시트·그림을 지워 용량을 줄인 뒤 다시 올립니다.",
      "두 번 이상 반복되면 고객센터에 파일명과 함께 알려 주세요.",
    ],
    retryHint: "same_file",
  },

  task_limit: {
    kind: "task_limit",
    title: "동시에 돌릴 수 있는 작업 수를 넘었어요",
    summary: "한 계정이 한 번에 대기·처리할 수 있는 작업 수에는 상한이 있습니다. 앞선 작업이 끝나면 다시 요청할 수 있습니다.",
    steps: [
      "작업내역에서 '처리중'·'대기중' 작업이 끝날 때까지 기다립니다.",
      "급하지 않은 대기 작업은 취소해 자리를 비웁니다.",
      "한 번에 올리는 파일 수를 줄여서 나눠 실행합니다.",
    ],
    retryHint: "same_file",
  },

  range_too_large: {
    kind: "range_too_large",
    title: "시트 범위가 너무 커요",
    summary:
      "엑셀자동화는 시트당 10,000행·ZZ열까지 처리합니다. 보통은 실제 내역이 큰 게 아니라, 시트 맨 아래나 오른쪽 끝에 잘못 들어간 값·수식·서식이 범위를 늘린 경우입니다.",
    steps: [
      "엑셀에서 해당 시트를 열고 Ctrl+End 를 눌러 '마지막 셀' 로 이동합니다. 데이터보다 훨씬 아래·오른쪽이면 그 사이에 불필요한 내용이 있는 것입니다.",
      "데이터 아래쪽 행 전체와 오른쪽 열 전체를 선택해 삭제(행/열 삭제 — 지우기가 아니라 삭제)한 뒤 저장합니다.",
      "저장 후 다시 Ctrl+End 를 눌러 마지막 셀이 데이터 끝 근처로 돌아왔는지 확인합니다.",
      "내역이 정말 1만 행을 넘는다면 시트를 공종별로 나눠 각각 처리합니다.",
      "고친 파일을 다시 업로드해 실행합니다.",
    ],
    figure: "ctrl_end",
    figureAfterStep: 1,
    retryHint: "fixed_file",
  },

  time_limit: {
    kind: "time_limit",
    title: "처리 시간이 한도(1시간)를 넘어요",
    summary:
      "작업 하나는 최대 1시간까지만 돌릴 수 있어요. 시트 안에 도형·그림·메모 상자가 수백 개 있거나, 스크롤 범위(사용 영역)가 데이터보다 훨씬 넓게 잡혀 있거나, 수식·이름정의가 수만 개면 행 하나 끼울 때마다 그만큼을 전부 밀어 써야 해서 몇 시간씩 걸립니다. 같은 파일을 같은 선택으로 다시 올리면 결과도 같으니 아래처럼 파일을 가볍게 만들거나 시트를 줄여 주세요.",
    steps: [
      "안내문에 '시트를 N개씩 나눠'가 있으면 그 개수만큼만 골라 실행하고, 끝나면 같은 파일을 다시 올려 다음 시트들을 실행합니다. 실행 전 '한도에 맞춰 앞쪽 시트만 남기기' 버튼을 누르면 첫 묶음이 자동으로 골라집니다.",
      "시트 선택에서 '전체 선택'을 풀고, 두 줄로 나눌 내역서 시트만 고릅니다(일위대가·단가산출·수량산출서·갑지 등은 제외).",
      "스크롤 범위 정리: 엑셀에서 시트를 열고 Ctrl+End 를 눌러 '마지막 셀'로 이동합니다. 데이터보다 훨씬 아래·오른쪽이면 데이터 아래쪽 행 전체와 오른쪽 열 전체를 선택해 삭제(지우기가 아니라 행/열 삭제)하고 저장합니다. 세로·가로 스크롤 막대가 짧아져 있으면 범위가 넓게 잡힌 신호입니다.",
      "도형·그림 정리: 시트에서 F5(이동) → '옵션' → '개체' → 확인을 누르면 시트 안의 도형·그림·메모 상자가 모두 선택됩니다. 내역에 필요 없는 것이면 Delete 로 지우고 저장합니다(두 줄 나누기 결과에는 도형이 필요 없습니다).",
      "파일에 '다른 통합문서 참조'나 이름정의(수식 → 이름 관리자)가 수천 개면, 값 붙여넣기로 정리한 사본을 올리면 훨씬 빨라집니다.",
      "접수 직후 '예상 처리 시간' 안내로 중단됐거나 '진행 속도로 보아' 안내로 중단된 경우도 같은 방법입니다. 정리한 사본(또는 시트를 줄인 선택)으로 다시 실행해 주세요.",
    ],
    figure: "ctrl_end",
    figureAfterStep: 3,
    retryHint: "fixed_file",
  },

  external_link: {
    kind: "external_link",
    title: "다른 통합문서를 참조하는 연결이 깨져 있어요",
    summary:
      "다른 엑셀 파일을 참조하는 수식·연결이 남아 있는데 그 원본 파일을 찾을 수 없어서 저장이 거부됐습니다. 연결을 끊고 값으로 바꾸면 해결됩니다.",
    steps: [
      "엑셀에서 파일을 열고 '데이터' 탭 › '쿼리 및 연결'(또는 '연결 편집')을 엽니다.",
      "목록에 있는 외부 연결을 모두 선택하고 '연결 끊기' 를 누릅니다. (참조 수식은 현재 값으로 바뀝니다.)",
      "그래도 남아 있으면 Ctrl+F 로 '[' 또는 '.xlsx]' 를 찾아 다른 파일을 가리키는 수식을 값으로 붙여넣습니다.",
      "'다른 이름으로 저장' 으로 새 파일(.xlsx)로 저장한 뒤 다시 업로드합니다.",
    ],
    figure: "external_link",
    figureAfterStep: 2,
    retryHint: "fixed_file",
  },

  drawing: {
    kind: "drawing",
    title: "그림·도형 때문에 처리하지 못했어요",
    summary:
      "시트에 들어 있는 그림·도형·차트·텍스트 상자가 처리를 막았습니다. 내역서 자동화에는 필요 없는 개체이므로 지우고 다시 올리면 됩니다.",
    steps: [
      "엑셀에서 해당 시트를 열고 '홈' 탭 › '찾기 및 선택' › '개체 선택' 을 누릅니다.",
      "시트 전체를 드래그해 개체(그림·도형·차트)를 한꺼번에 선택한 뒤 Delete 키로 지웁니다. (또는 F5 › '옵션' › '개체' › 확인 › Delete)",
      "회사 로고처럼 꼭 필요한 그림은 처리 후 결과 파일에 다시 넣어 주세요.",
      "'다른 이름으로 저장' 으로 새 파일(.xlsx)로 저장한 뒤 다시 업로드합니다.",
    ],
    retryHint: "fixed_file",
  },

  protected: {
    kind: "protected",
    title: "암호·보호가 걸린 파일이에요",
    summary: "통합문서 암호나 시트 보호가 걸려 있으면 내용을 읽거나 고칠 수 없습니다. 보호를 풀고 다시 올려 주세요.",
    steps: [
      "열기 암호가 있으면: '파일' › '정보' › '통합 문서 보호' › '암호를 사용하여 암호화' 에서 암호를 지우고 저장합니다.",
      "시트 보호가 있으면: '검토' 탭 › '시트 보호 해제' 를 누릅니다. (여러 시트면 각각)",
      "통합문서 구조 보호가 있으면: '검토' 탭 › '통합 문서 보호' 를 눌러 해제합니다.",
      "저장한 뒤 다시 업로드해 실행합니다.",
    ],
    retryHint: "fixed_file",
  },

  cost_ratio_missing: {
    kind: "cost_ratio_missing",
    title: "원가계산서에서 '구성비' 열을 찾지 못했어요",
    summary:
      "원가계산서 시트에서 비목별 요율이 적힌 '구성비(%)' 열을 찾지 못했습니다. 제경비 요율을 연결하려면 이 열이 필요합니다.",
    steps: [
      "원가계산서 시트의 머리글에 '구성비' 또는 '구성비(%)', '요율' 이라고 적힌 열이 있는지 확인합니다. 없으면 금액 열 옆에 추가합니다.",
      "머리글 글자가 두 셀로 나뉘어 있거나(예: '구성' / '비') 줄바꿈·공백으로 쪼개져 있지 않은지 확인합니다.",
      "구성비 열이 필요 없는 작업이라면, 원가계산서 시트를 선택에서 빼고 집계표·내역서만 처리합니다.",
      "고친 파일을 다시 업로드해 실행합니다.",
    ],
    retryHint: "fixed_file",
  },

  statement_not_found: {
    kind: "statement_not_found",
    title: "내역서를 인식하지 못했어요",
    summary:
      "선택한 시트에서 내역서 표(품명·규격·단위·수량·재료비·노무비·경비·합계)를 찾지 못했습니다. 대개 시트를 잘못 골랐거나 머리글 모양이 달라서 생깁니다.",
    steps: [
      "처리할 시트만 고릅니다: 내역서·집계표 시트는 체크하고, 수량산출서·갑지·목차·단가산출 같은 시트는 체크를 풉니다.",
      "내역서 머리글이 아래 그림처럼 되어 있는지 확인합니다. 재료비·노무비·경비·합계는 각각 '단가 / 금액' 두 칸이어야 합니다.",
      "머리글 글자가 병합·줄바꿈·공백으로 쪼개져 있지 않은지 확인합니다. (예: '재료비' 가 '재 료 비' 나 두 셀로 나뉘면 인식이 안 됩니다.)",
      "머리글 위에 공사명 같은 큰 제목 행이 있어도 괜찮지만, 표 머리글 자체는 빈 행 없이 한 덩어리로 데이터 바로 위에 있어야 합니다.",
      "고친 파일을 저장하고 다시 업로드해 실행합니다.",
    ],
    figure: "header_structure",
    figureAfterStep: 2,
    retryHint: "fixed_file",
  },

  save_failed: {
    kind: "save_failed",
    title: "파일 형식 문제로 저장하지 못했어요",
    summary:
      "처리는 됐지만 결과 파일을 저장하는 단계에서 엑셀 라이브러리가 파일을 거부했습니다. 오래된 형식(.xls)·Strict Open XML·한셀 저장 파일, 행 전체에 걸린 하이퍼링크·서식, 배열 수식이 많은 시트에서 자주 생깁니다.",
    steps: [
      "엑셀에서 파일을 열고 '파일' › '다른 이름으로 저장' › 형식을 'Excel 통합 문서(*.xlsx)' 로 골라 새 파일로 저장합니다. (.xls·Strict Open XML·한셀 파일은 꼭 변환)",
      "행이나 열 전체에 걸린 하이퍼링크·조건부 서식이 있으면 지웁니다. ('홈' › '조건부 서식' › '규칙 지우기' › '시트 전체')",
      "배열 수식({…})이나 복잡한 수식이 많은 시트는 전체 복사 › '값으로 붙여넣기' 로 바꿉니다.",
      "그래도 같으면 시트 탭을 모두 선택하고 마우스 오른쪽 › '이동/복사' › '(새 통합 문서)' 로 옮겨 새 파일로 저장합니다.",
      "불필요한 시트·그림·외부 연결을 정리한 뒤 새로 저장한 파일을 다시 업로드합니다.",
    ],
    retryHint: "fixed_file",
  },

  general: {
    kind: "general",
    title: "처리 중 오류가 생겼어요",
    summary:
      "정해진 유형에 들어가지 않는 오류입니다. 같은 파일을 그대로 다시 올리면 대개 같은 결과가 나오니, 아래 순서대로 파일을 새로 만들어 올려 주세요.",
    steps: [
      "엑셀에서 '다른 이름으로 저장' › 'Excel 통합 문서(*.xlsx)' 로 새 파일로 저장합니다.",
      "그래도 같으면 시트 탭을 모두 선택(첫 탭 클릭 › Shift 누른 채 마지막 탭 클릭)하고 마우스 오른쪽 › '이동/복사' › 대상 '(새 통합 문서)' › '복사본 만들기' 체크로 새 통합 문서를 만들어 저장합니다. 파일 안의 어긋난 구성 요소가 함께 정리됩니다.",
      "불필요한 시트(수량산출서·갑지·목차 등)는 처리 대상에서 빼고, 그림·도형·외부 연결은 지웁니다.",
      "새로 만든 파일을 업로드해 실행합니다.",
      "그래도 안 되면 고객센터에 파일명과 아래 안내문을 그대로 알려 주세요.",
    ],
    retryHint: "fixed_file",
  },
};

/** 판별 규칙 — 위에서부터 처음 맞는 규칙을 쓴다. */
const RULES: { kind: ExcelFixGuideKind; patterns: RegExp[] }[] = [
  { kind: "no_permission", patterns: [/구매\s*(가\s*)?필요/, /이용권/, /권한이\s*없/] },
  { kind: "input_missing", patterns: [/시트를\s*선택해/, /파일\s*정보가\s*없/] },
  { kind: "server_restart", patterns: [/서버.{0,6}재시작/] },
  {
    kind: "time_limit",
    patterns: [/처리\s*시간이.{0,12}넘어\s*중단/, /예상\s*처리\s*시간이.{0,20}한도/, /진행\s*속도로\s*보아/, /한도로\s*중단된\s*파일/, /recent_limit_failure/],
  },
  { kind: "transient", patterns: [/시간이\s*초과/, /timeout/i, /timed?\s*out/i] },
  { kind: "task_limit", patterns: [/처리\s*요청이\s*거절/, /동시\s*대기\s*가능/, /task_limit/, /잠시\s*후\s*다시\s*시도/] },
  { kind: "range_too_large", patterns: [/데이터\s*범위가\s*너무\s*큽/, /10,?000\s*행/, /ZZ\s*열/] },
  { kind: "external_link", patterns: [/다른\s*통합\s*문서를\s*참조/, /External\s*reference/i] },
  { kind: "drawing", patterns: [/파일\s*(안)?에\s*이미지/, /이미지가\s*포함/, /이미지\s*·\s*도형/, /그림/, /closed\s*Stream/i] },
  { kind: "protected", patterns: [/암호/, /시트\s*보호/, /통합\s*문서\s*보호/, /보호된/, /encrypted/i, /password/i] },
  { kind: "cost_ratio_missing", patterns: [/구성비.{0,8}찾을\s*수\s*없/] },
  {
    kind: "statement_not_found",
    patterns: [/내역서를\s*찾지\s*못/, /내역서를\s*인식하지\s*못/, /구조가\s*올바르지\s*않/, /필요한\s*항목.{0,40}찾을\s*수\s*없/],
  },
  { kind: "save_failed", patterns: [/저장하는\s*중\s*오류/, /저장할\s*수\s*없/] },
];

/** error_message 로 오류 유형을 판별해 안내를 돌려준다. 비어 있으면 "일반". */
export function matchExcelFixGuide(errorMessage: string | null | undefined): ExcelFixGuide {
  const text = (errorMessage || "").trim();
  if (!text) return EXCEL_FIX_GUIDES.general;
  for (const rule of RULES) {
    if (rule.patterns.some((re) => re.test(text))) return EXCEL_FIX_GUIDES[rule.kind];
  }
  return EXCEL_FIX_GUIDES.general;
}
